import { z } from "zod";

// Helper function to convert string to boolean
function toBoolean(val: string | boolean) {
  if (typeof val === "boolean") return val;
  return val.toLowerCase() === "true" || val === "1";
}

// Helper function to convert string to number
function toNumber(val: string | number) {
  if (typeof val === "number") return val;
  // Remove currency symbols, commas and spaces
  return Number(val.replace(/[£$,\s]/g, ""));
}

// Helper function to create unit fields
function createUnitFields(numberOfUnits: number, securityIndex?: number) {
  const unitFields: Record<string, any> = {};

  for (let i = 1; i <= numberOfUnits; i++) {
    const fieldKey =
      securityIndex !== undefined ? `unit_number_${securityIndex + 1}_${i}` : `unit_number_${i}`;
    unitFields[fieldKey] = z.string().min(1, "Unit number is required");

    const valueKey =
      securityIndex !== undefined ? `unit_value_${securityIndex + 1}_${i}` : `unit_value_${i}`;
    unitFields[valueKey] = z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(
        val => val !== null && !isNaN(val) && val >= 0,
        `Unit ${i} value must be a positive number`
      )
      .optional();

    const squareFeetKey =
      securityIndex !== undefined
        ? `unit_square_feet_${securityIndex + 1}_${i}`
        : `unit_square_feet_${i}`;
    unitFields[squareFeetKey] = z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(
        val => val !== null && !isNaN(val) && val >= 0,
        `Unit ${i} square feet must be a positive number`
      )
      .optional();

    const soldKey =
      securityIndex !== undefined ? `unit_sold_${securityIndex + 1}_${i}` : `unit_sold_${i}`;
    unitFields[soldKey] = z.union([z.boolean(), z.string()]).transform(toBoolean).optional();
  }

  return unitFields;
}

// Helper function to create security fields
function createSecurityFields(securityIndex: number) {
  const fields: Record<string, any> = {};
  const suffix = `_${securityIndex + 1}`;

  fields[`postcode${suffix}`] = z.string().min(1, "Postcode is required");
  fields[`title_number${suffix}`] = z.string().min(1, "Title number is required");
  fields[`property_type${suffix}`] = z.string().min(1, "Property type is required");
  fields[`sub_type${suffix}`] = z.string().min(1, "Property sub-type is required");
  fields[`development${suffix}`] = z
    .union([z.boolean(), z.string(), z.null()])
    .transform(val => (val === null ? null : toBoolean(val)))
    .refine(val => val !== undefined && val !== null, "Development is required");
  fields[`development_complete${suffix}`] = z
    .union([z.boolean(), z.string()])
    .transform(toBoolean)
    .nullish();
  fields[`completion_date${suffix}`] = z.string().optional();
  fields[`insurance_expiry${suffix}`] = z.string().min(1, "Insurance expiry date is required");
  fields[`square_feet${suffix}`] = z
    .union([z.number(), z.string()])
    .transform(toNumber)
    .refine(
      val => val !== null && !isNaN(val) && val >= 0,
      "Square feet must be a positive number"
    );
  fields[`number_of_units${suffix}`] = z
    .union([z.number(), z.string()])
    .transform(toNumber)
    .refine(val => val !== null && !isNaN(val), "Invalid number")
    .optional();

  return fields;
}

// Default schema with root security
export const propertyFieldsSchema = createPropertyFieldsSchema();

// Type inference
export type PropertyFieldsFormData = z.infer<typeof propertyFieldsSchema>;

function createPropertyFieldsSchema() {
  const baseFields = {
    security: z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(val => val !== null && !isNaN(val), "Invalid number")
      .optional(),
    postcode: z.string().min(1, "Postcode is required"),
    title_number: z.string().min(1, "Title number is required"),
    property_type: z.string().min(1, "Property type is required"),
    sub_type: z.string().min(1, "Property sub-type is required"),
    development: z
      .union([z.boolean(), z.string(), z.null()])
      .transform(val => (val === null ? null : toBoolean(val)))
      .refine(val => val !== undefined && val !== null, "Development is required"),
    development_complete: z.union([z.boolean(), z.string()]).transform(toBoolean).nullish(),
    completion_date: z.string().optional(),
    purchase_price: z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(val => val !== null && !isNaN(val), "Purchase price is required")
      .refine(
        val => val !== null && !isNaN(val) && val >= 0,
        "Purchase price must be a positive number"
      ),
    recent_valuation: z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(val => val !== null && !isNaN(val), "Invalid number")
      .optional(),
    gdv: z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(val => val !== null && !isNaN(val), "Invalid number")
      .optional(),
    draws_to_date: z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(val => val !== null && !isNaN(val), "Invalid number")
      .optional(),
    remaining_to_draw: z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(val => val !== null && !isNaN(val), "Invalid number")
      .optional(),
    insurance_expiry: z.string().min(1, "Insurance expiry date is required"),
    square_feet: z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(
        val => val !== null && !isNaN(val) && val >= 0,
        "Square feet must be a positive number"
      ),
    number_of_units: z
      .union([z.number(), z.string()])
      .transform(toNumber)
      .refine(val => val !== null && !isNaN(val), "Invalid number")
      .optional(),
  };

  return z
    .object(baseFields)
    .passthrough()
    .superRefine((data: Record<string, any>, ctx) => {
      // Validate base units
      const numberOfUnits = data.number_of_units ? Number(data.number_of_units) : 0;
      if (numberOfUnits > 0) {
        const unitFields = createUnitFields(numberOfUnits);
        const unitSchema = z.object(unitFields);
        const result = unitSchema.safeParse(data);
        if (!result.success) {
          result.error.errors.forEach(err => {
            ctx.addIssue({
              path: err.path,
              message: err.message,
              code: z.ZodIssueCode.custom,
            });
          });
        }
      }

      // Validate security fields and their units
      const securityCount = data.security ? Number(data.security) : 0;
      if (securityCount > 0) {
        for (let i = 0; i < securityCount; i++) {
          // Validate security fields
          const securityFields = createSecurityFields(i);
          const securitySchema = z.object(securityFields);
          const securityResult = securitySchema.safeParse(data);
          if (!securityResult.success) {
            securityResult.error.errors.forEach(err => {
              ctx.addIssue({
                path: err.path,
                message: err.message,
                code: z.ZodIssueCode.custom,
              });
            });
          }

          // Validate security units
          const securityUnits = data[`number_of_units_${i + 1}`]
            ? Number(data[`number_of_units_${i + 1}`])
            : 0;
          if (securityUnits > 0) {
            const unitFields = createUnitFields(securityUnits, i);
            const unitSchema = z.object(unitFields);
            const result = unitSchema.safeParse(data);
            if (!result.success) {
              result.error.errors.forEach(err => {
                ctx.addIssue({
                  path: err.path,
                  message: err.message,
                  code: z.ZodIssueCode.custom,
                });
              });
            }
          }
        }
      }
    });
}
